import { useEffect, useState } from "react";

interface CategoryFilterBarProps {
  letters: string[];
  query: string;
  onQueryChange: (value: string) => void;
  matchCount: number;
  totalCategories: number;
}

export default function CategoryFilterBar({
  letters,
  query,
  onQueryChange,
  matchCount,
  totalCategories,
}: CategoryFilterBarProps) {
  const [active, setActive] = useState(letters[0] ?? "");
  const normalized = query.trim().toLowerCase();
  const isSearching = normalized.length > 0;

  useEffect(() => {
    if (isSearching) return;
    const sections = letters
      .map((letter) => document.getElementById(`letter-${letter}`))
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id.replace("letter-", ""));
          }
        });
      },
      { rootMargin: "-260px 0px -70% 0px", threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [letters, isSearching]);

  return (
    <div className="sticky top-[72px] z-40 border-y border-background-200 bg-background-50/95 backdrop-blur md:top-[116px]">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-3 md:px-6">
        <div className="relative">
          <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search categories by name — try “salon”, “ac repair”, “legal”, “fishing”…"
            aria-label="Search business categories by name"
            className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-24 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
          {isSearching ? (
            <button
              type="button"
              onClick={() => onQueryChange("")}
              aria-label="Clear category search"
              className="absolute right-2.5 top-1/2 inline-flex -translate-y-1/2 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-2.5 py-1 text-xs font-semibold text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-close-line text-sm" />
              </span>
              Clear
            </button>
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {isSearching ? (
            <p className="text-xs font-medium text-foreground-600">
              <span className="font-bold text-primary-700">{matchCount}</span> of{" "}
              {totalCategories.toLocaleString("en-IN")} categories match &ldquo;
              <span className="font-semibold text-foreground-800">{query.trim()}</span>&rdquo;
            </p>
          ) : (
            <>
              <span className="mr-1 hidden text-xs font-semibold uppercase tracking-wide text-foreground-500 sm:inline">
                Jump to
              </span>
              {letters.map((letter) => {
                const isActive = active === letter;
                return (
                  <a
                    key={letter}
                    href={`#letter-${letter}`}
                    aria-label={`Go to categories starting with ${letter}`}
                    className={`flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md px-2 text-xs font-bold uppercase transition-colors duration-200 ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                    }`}
                  >
                    {letter}
                  </a>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}